import ButtonPrimary from "@rentiohq/shared-frontend/dist/components/components/ButtonPrimary";
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { appendQueryParams } from "@rentiohq/shared-frontend/dist/utils/url.utils";
import { ASSETS } from "assets";
import { AppBanner } from "components/AppBanner";
import { useEffect } from "react";
import { isIOS, isAndroid } from "react-device-detect";
import { useLocation, useNavigate } from "react-router";
import { StringParam } from "serialize-query-params";
import * as S from "./Home.styles";

// Possible links
// https://pay.rentio.be/payment/123
// https://dashboard.rentio.be/tasks/123
// https://rentio.be/tasks/123

// A link can carry data in its own fragment (link.rentio.be/?link=...#token=...), which the
// browser never sends to a server, so credentials go there instead of in `link` (DEV-3357).
// Every redirect hands that fragment on to the target, unless the target has one of its own,
// and only to a Rentio host: `link` is not validated, and a tampered one must not receive
// the credentials.
const isRentioHost = (target: string) => {
  try {
    const { protocol, hostname } = new URL(target, window.location.origin);

    return (
      protocol === "https:" &&
      ["rentio.be", "rentio-internal.be"].some(
        domain => hostname === domain || hostname.endsWith(`.${domain}`),
      )
    );
  } catch {
    return false;
  }
};

const withLinkFragment = (target: string) => {
  const { hash } = window.location;
  if (!hash || target.includes("#") || !isRentioHost(target)) {
    return target;
  }

  return `${target}${hash}`;
};

export const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [queryParams] = useQueryParams({
    link: StringParam,
  });
  const { link } = queryParams;

  // const isRentioPay = link?.includes("rentio.be") && link.includes("pay");
  const isRentioDashboard =
    (link?.includes("rentio.be") || link?.includes("rentio-internal.be")) &&
    link.includes("dashboard");
  // const isRentioMain = link?.includes("rentio.be") && link.includes("tasks");

  const isDev =
    (link?.includes("rentio.be") || link?.includes("rentio-internal.be")) &&
    link.includes("dev");
  const isStaging =
    (link?.includes("rentio.be") || link?.includes("rentio-internal.be")) &&
    link.includes("stag");
  const isDemo =
    (link?.includes("rentio.be") || link?.includes("rentio-internal.be")) &&
    link.includes("demo");

  const immediateOpenLink = link && !isIOS && !isAndroid;

  // Hooks
  useEffect(() => {
    if (!link) {
      return;
    }

    if (!immediateOpenLink) {
      return;
    }

    // Map deeplinks to Dashboard links
    if (isRentioDashboard) {
      const parsedLink = new URL(link);
      const { pathname } = parsedLink;
      const pathComponents = (
        pathname.startsWith("/") ? pathname.slice(1) : pathname
      ).split("/");
      const scope = pathComponents[0];

      switch (scope) {
        case "index_contract":
          const contractId = parsedLink.searchParams.get("contractId");
          document.location.href = `${parsedLink.origin}/follow-up/indexable-contracts?contractId=${contractId}`;
          return;

        default:
          break;
      }
    }

    document.location.href = withLinkFragment(link);
  }, [link]);

  useEffect(() => {
    if (!link) {
      return;
    }

    if (immediateOpenLink) {
      return;
    }

    let requestedPathname = "/";
    if (isDev) {
      requestedPathname = "/open-beta-dev";
    }
    if (isStaging) {
      requestedPathname = "/open-beta-stag";
    }
    if (isDemo) {
      requestedPathname = "/open-beta-demo";
    }

    if (location.pathname === "/") {
      // Same origin, so the fragment comes along when the link itself is a Rentio one.
      const path = appendQueryParams({
        path: requestedPathname,
        queryParams: {
          link,
        },
      });
      navigate(isRentioHost(link) ? `${path}${window.location.hash}` : path);
    }
  }, [link]);

  // Render helpers
  const renderNavigateToIOSApp = () => {
    if (!link || !deeplinkIos || !isIOS) {
      return null;
    }

    return (
      <ButtonPrimary
        title={getLocalizedText("link.cta.open_in_app")}
        onPress={() => {
          if (!deeplinkIos) {
            return;
          }

          document.location = deeplinkIos;
        }}
      />
    );
  };

  // Render
  if (link && immediateOpenLink) {
    return null;
  }

  const currentUrl = new URL(document.location.href);
  const deeplinkIos =
    currentUrl.origin.includes("localhost") ||
    // Fallback if IP is used instead of localhost
    !currentUrl.origin.includes("rentio")
      ? document.location.href.replace(currentUrl.origin, "rentio://rentio.be")
      : document.location.href.replace("https://", "rentio://");

  return (
    <S.Wrap>
      <S.RentioLogo src={ASSETS.IMG_RENTIO_LOGO} />
      <S.InnerWrap>
        <AppBanner />

        <S.ButtonsWrap>
          {renderNavigateToIOSApp()}

          {link && (
            <ButtonPrimary
              isInverted={true}
              title={getLocalizedText("link.cta.open_online")}
              onPress={() => {
                if (link) {
                  document.location.href = withLinkFragment(link);
                }
              }}
            />
          )}
        </S.ButtonsWrap>

        {link && <S.LinkInfo href={link}>{link}</S.LinkInfo>}

        {!link && <S.LinkNotFound>Link not found</S.LinkNotFound>}

        {/* <span>{"is dev: " + isDev}</span>
        <span>{"is staging: " + isStaging}</span>
        <span>{"is demo: " + isDemo}</span>
        <span>{"is rentio pay: " + isRentioPay}</span>
        <span>{"immediateOpenLink: " + immediateOpenLink}</span>
        <span>{"link: " + link}</span>
        <span>{"location.pathname: " + location.pathname}</span> */}
      </S.InnerWrap>
    </S.Wrap>
  );
};
